import { differenceInMinutes, addMinutes, isSameDay, format } from 'date-fns';

/**
 * Family Coordination Service
 * 
 * Intelligent cross-member schedule optimization and conflict detection
 * for seamless family coordination.
 */
class FamilyCoordinationService {
  constructor() {
    this.coordinationRules = new Map();
    this.conflictPatterns = new Map();
    this.resolutionHistory = new Map();
  }

  /**
   * Analyze family coordination opportunities and conflicts
   */
  analyzeCoordination(familyMembers, familyCalendar, tasks, activities) {
    // Build member profiles
    const memberProfiles = this.buildMemberProfiles(familyMembers, familyCalendar, tasks);
    
    // Detect conflicts
    const conflicts = this.detectAllConflicts(memberProfiles, familyCalendar);
    
    // Find coordination opportunities
    const opportunities = this.findCoordinationOpportunities(memberProfiles, activities);
    
    // Generate optimization suggestions
    const optimizations = this.generateOptimizations(memberProfiles, conflicts, opportunities);
    
    // Calculate family coordination score
    const coordinationScore = this.calculateCoordinationScore(memberProfiles, conflicts);

    return {
      memberProfiles,
      conflicts,
      opportunities,
      optimizations,
      coordinationScore,
      insights: this.generateCoordinationInsights(memberProfiles, conflicts, opportunities)
    };
  }

  /**
   * Build comprehensive member profiles
   */
  buildMemberProfiles(familyMembers, familyCalendar, tasks) {
    const profiles = new Map();

    familyMembers.forEach(member => {
      const memberEvents = this.getMemberEvents(member.id, familyCalendar);
      const memberTasks = this.getMemberTasks(member.id, tasks);
      const commitments = this.analyzeCommitments(memberEvents, memberTasks);
      const availability = this.calculateAvailability(memberEvents);
      const preferences = this.extractPreferences(member, memberEvents);

      profiles.set(member.id, {
        member,
        events: memberEvents,
        tasks: memberTasks,
        commitments,
        availability,
        preferences,
        role: member.isParent ? 'parent' : 'child',
        statistics: {
          weeklyCommitments: this.countWeeklyCommitments(memberEvents),
          averageDailyBusy: this.calculateAverageDailyBusy(memberEvents),
          preferredTimes: this.identifyPreferredTimes(memberEvents)
        }
      });
    });

    return profiles;
  }

  /**
   * Detect all types of conflicts
   */
  detectAllConflicts(memberProfiles, familyCalendar) {
    const conflicts = [];

    // Time conflicts (double-booking)
    const timeConflicts = this.detectTimeConflicts(memberProfiles);
    conflicts.push(...timeConflicts);

    // Resource conflicts (car, location)
    const resourceConflicts = this.detectResourceConflicts(familyCalendar);
    conflicts.push(...resourceConflicts);

    // Logistics conflicts (impossible travel time)
    const logisticsConflicts = this.detectLogisticsConflicts(memberProfiles);
    conflicts.push(...logisticsConflicts);

    // Parent availability conflicts
    const parentConflicts = this.detectParentAvailabilityConflicts(memberProfiles);
    conflicts.push(...parentConflicts);

    return this.rankConflictsBySeverity(conflicts);
  }

  /**
   * Find coordination opportunities
   */
  findCoordinationOpportunities(memberProfiles, activities) {
    const opportunities = [];

    // Family time opportunities
    const familyTime = this.findFamilyTimeSlots(memberProfiles);
    opportunities.push(...familyTime.map(slot => ({
      type: 'family-time',
      ...slot,
      suggestions: this.generateFamilyActivitySuggestions(slot, activities)
    })));

    // Carpool opportunities
    const carpool = this.findCarpoolOpportunities(memberProfiles);
    opportunities.push(...carpool);

    // Task delegation opportunities
    const delegation = this.findDelegationOpportunities(memberProfiles);
    opportunities.push(...delegation);

    // Batch errand opportunities
    const errands = this.findErrandBatchingOpportunities(memberProfiles);
    opportunities.push(...errands);

    return this.rankOpportunitiesByImpact(opportunities);
  }

  /**
   * Generate optimization suggestions
   */
  generateOptimizations(memberProfiles, conflicts, opportunities) {
    const optimizations = [];

    // Conflict resolution suggestions
    conflicts.forEach(conflict => {
      const resolutions = this.generateConflictResolutions(conflict, memberProfiles);
      optimizations.push({
        type: 'conflict-resolution',
        target: conflict,
        resolutions,
        priority: conflict.severity,
        estimatedImpact: this.estimateResolutionImpact(conflict, resolutions)
      });
    });

    // Opportunity realization suggestions
    opportunities.forEach(opportunity => {
      const actions = this.generateOpportunityActions(opportunity, memberProfiles);
      optimizations.push({
        type: 'opportunity-realization',
        target: opportunity,
        actions,
        priority: opportunity.impact,
        estimatedBenefit: this.estimateOpportunityBenefit(opportunity)
      });
    });

    // Proactive optimizations
    const proactive = this.generateProactiveOptimizations(memberProfiles);
    optimizations.push(...proactive);

    return this.prioritizeOptimizations(optimizations);
  }

  /**
   * Conflict detection methods
   */
  detectTimeConflicts(memberProfiles) {
    const conflicts = [];

    memberProfiles.forEach((profile, memberId) => {
      const events = profile.events;
      
      // Check for overlapping events
      for (let i = 0; i < events.length; i++) {
        for (let j = i + 1; j < events.length; j++) {
          if (this.eventsOverlap(events[i], events[j])) {
            conflicts.push({
              type: 'time-conflict',
              severity: 'high',
              member: profile.member,
              events: [events[i], events[j]],
              overlap: this.calculateOverlap(events[i], events[j]),
              message: `${profile.member.name} has overlapping events`
            });
          }
        }
      }
    });

    return conflicts;
  }

  detectResourceConflicts(familyCalendar) {
    const conflicts = [];
    const resourceUsage = new Map();

    // Track resource usage by time
    familyCalendar.forEach(event => {
      if (event.resources) {
        event.resources.forEach(resource => {
          const key = `${resource}-${event.date}-${event.time}`;
          if (!resourceUsage.has(key)) {
            resourceUsage.set(key, []);
          }
          resourceUsage.get(key).push(event);
        });
      }
    });

    // Find conflicts
    resourceUsage.forEach((events, key) => {
      if (events.length > 1) {
        const [resource] = key.split('-');
        conflicts.push({
          type: 'resource-conflict',
          severity: 'medium',
          resource,
          events,
          message: `Multiple events need ${resource} at the same time`
        });
      }
    });

    return conflicts;
  }

  detectLogisticsConflicts(memberProfiles) {
    const conflicts = [];

    memberProfiles.forEach(profile => {
      const events = profile.events.sort((a, b) => 
        new Date(a.date + ' ' + a.time) - new Date(b.date + ' ' + b.time)
      );

      for (let i = 0; i < events.length - 1; i++) {
        const current = events[i];
        const next = events[i + 1];
        
        if (isSameDay(new Date(current.date), new Date(next.date))) {
          const travelTime = this.estimateTravelTime(current.location, next.location);
          const gap = this.calculateEventGap(current, next);
          
          if (gap < travelTime) {
            conflicts.push({
              type: 'logistics-conflict',
              severity: 'high',
              member: profile.member,
              events: [current, next],
              travelTime,
              availableTime: gap,
              message: `Not enough time to travel between events`
            });
          }
        }
      }
    });

    return conflicts;
  }

  detectParentAvailabilityConflicts(memberProfiles) {
    const conflicts = [];
    const parentProfiles = Array.from(memberProfiles.values())
      .filter(p => p.role === 'parent');
    const childProfiles = Array.from(memberProfiles.values())
      .filter(p => p.role === 'child');

    childProfiles.forEach(childProfile => {
      childProfile.events.forEach(event => {
        if (event.requiresParent) {
          const availableParent = parentProfiles.find(parent => 
            this.isParentAvailable(parent, event)
          );
          
          if (!availableParent) {
            conflicts.push({
              type: 'parent-availability',
              severity: 'high',
              child: childProfile.member,
              event,
              message: `No parent available for ${childProfile.member.name}'s ${event.title}`
            });
          }
        }
      });
    });

    return conflicts;
  }

  /**
   * Opportunity finding methods
   */
  findFamilyTimeSlots(memberProfiles) {
    const slots = [];
    const allMembers = Array.from(memberProfiles.values());
    
    // Look for common free time
    const timeSlots = this.generateTimeSlots();
    
    timeSlots.forEach(slot => {
      const availableMembers = allMembers.filter(profile => 
        this.isMemberAvailable(profile, slot)
      );
      
      if (availableMembers.length === allMembers.length) {
        slots.push({
          timeSlot: slot,
          duration: slot.duration,
          allMembersAvailable: true,
          quality: this.assessTimeSlotQuality(slot, allMembers)
        });
      } else if (availableMembers.length >= allMembers.length * 0.75) {
        slots.push({
          timeSlot: slot,
          duration: slot.duration,
          availableMembers: availableMembers.map(p => p.member),
          missingMembers: allMembers
            .filter(p => !availableMembers.includes(p))
            .map(p => p.member),
          quality: this.assessTimeSlotQuality(slot, availableMembers)
        });
      }
    });

    return slots;
  }

  findCarpoolOpportunities(memberProfiles) {
    const opportunities = [];
    const allEvents = [];

    // Collect all events with locations
    memberProfiles.forEach((profile, memberId) => {
      profile.events.forEach(event => {
        if (event.location && event.location !== 'Home') {
          allEvents.push({
            ...event,
            member: profile.member
          });
        }
      });
    });

    // Group by time window and destination
    const timeWindows = this.groupEventsByTimeAndLocation(allEvents);
    
    timeWindows.forEach(window => {
      if (window.events.length > 1) {
        const drivers = window.events
          .map(e => e.member)
          .filter(m => m.canDrive || m.isParent);
        
        if (drivers.length > 0) {
          opportunities.push({
            type: 'carpool',
            events: window.events,
            potentialDriver: drivers[0],
            participants: window.events.map(e => e.member),
            timeSaved: this.estimateCarpoolTimeSaved(window.events),
            impact: 'medium'
          });
        }
      }
    });

    return opportunities;
  }

  findDelegationOpportunities(memberProfiles) {
    const opportunities = [];
    
    // Find overloaded members
    const workloads = this.calculateMemberWorkloads(memberProfiles);
    const overloaded = workloads.filter(w => w.score > 0.8);
    const underutilized = workloads.filter(w => w.score < 0.3);
    
    overloaded.forEach(overloadedMember => {
      const delegatableTasks = this.identifyDelegatableTasks(
        overloadedMember.profile
      );
      
      underutilized.forEach(availableMember => {
        const compatibleTasks = delegatableTasks.filter(task => 
          this.canMemberHandleTask(availableMember.profile, task)
        );
        
        if (compatibleTasks.length > 0) {
          opportunities.push({
            type: 'delegation',
            from: overloadedMember.profile.member,
            to: availableMember.profile.member,
            tasks: compatibleTasks,
            workloadReduction: compatibleTasks.length / delegatableTasks.length,
            impact: 'medium'
          });
        }
      });
    });

    return opportunities;
  }

  findErrandBatchingOpportunities(memberProfiles) {
    const opportunities = [];
    
    memberProfiles.forEach(profile => {
      const errands = profile.events.filter(e => 
        e.category === 'errand' || e.type === 'shopping'
      );
      
      if (errands.length >= 2) {
        const grouped = this.groupErrandsByProximity(errands);
        
        grouped.forEach(group => {
          if (group.length >= 2) {
            opportunities.push({
              type: 'errand-batching',
              member: profile.member,
              errands: group,
              currentTime: this.calculateTotalErrandTime(group),
              optimizedTime: this.calculateBatchedErrandTime(group),
              timeSaved: this.calculateErrandTimeSaved(group),
              impact: 'low'
            });
          }
        });
      }
    });

    return opportunities;
  }

  /**
   * Resolution generation methods
   */
  generateConflictResolutions(conflict, memberProfiles) {
    const resolutions = [];

    switch (conflict.type) {
      case 'time-conflict':
        resolutions.push(...this.generateTimeConflictResolutions(conflict));
        break;
      case 'resource-conflict':
        resolutions.push(...this.generateResourceConflictResolutions(conflict));
        break;
      case 'logistics-conflict':
        resolutions.push(...this.generateLogisticsResolutions(conflict));
        break;
      case 'parent-availability':
        resolutions.push(...this.generateParentAvailabilityResolutions(conflict, memberProfiles));
        break;
    }

    return resolutions;
  }

  generateTimeConflictResolutions(conflict) {
    const resolutions = [];
    const [event1, event2] = conflict.events;

    // Time shifting
    if (this.canReschedule(event1)) {
      resolutions.push({
        action: 'reschedule',
        target: event1,
        suggestion: `Move "${event1.title}" 30 minutes earlier`,
        feasibility: this.assessRescheduleFeasibility(event1, -30)
      });
    }

    // Priority-based resolution
    if (event1.priority !== event2.priority) {
      const lowerPriority = event1.priority < event2.priority ? event1 : event2;
      resolutions.push({
        action: 'skip',
        target: lowerPriority,
        suggestion: `Skip lower priority event "${lowerPriority.title}"`,
        feasibility: 0.7
      });
    }

    // Delegation
    if (event1.delegatable || event2.delegatable) {
      resolutions.push({
        action: 'delegate',
        target: event1.delegatable ? event1 : event2,
        suggestion: `Delegate "${(event1.delegatable ? event1 : event2).title}" to another family member`,
        feasibility: 0.8
      });
    }

    return resolutions;
  }

  generateResourceConflictResolutions(conflict) {
    const resolutions = [];

    // Alternative resource
    resolutions.push({
      action: 'alternative-resource',
      suggestion: `Find alternative ${conflict.resource}`,
      options: this.suggestAlternativeResources(conflict.resource)
    });

    // Time staggering
    resolutions.push({
      action: 'stagger-times',
      suggestion: `Stagger event times to share ${conflict.resource}`,
      schedule: this.generateStaggeredSchedule(conflict.events)
    });

    return resolutions;
  }

  generateLogisticsResolutions(conflict) {
    const resolutions = [];
    const { events, travelTime, availableTime } = conflict;

    // Leave early
    const timeNeeded = travelTime - availableTime + 10; // 10 min buffer
    resolutions.push({
      action: 'adjust-timing',
      suggestion: `Leave ${events[0].title} ${timeNeeded} minutes early`,
      feasibility: this.canLeaveEarly(events[0], timeNeeded) ? 0.9 : 0.3
    });

    // Virtual attendance
    if (events[1].virtualOption) {
      resolutions.push({
        action: 'virtual-attendance',
        suggestion: `Attend "${events[1].title}" virtually`,
        feasibility: 0.95
      });
    }

    // Skip or delegate
    resolutions.push({
      action: 'skip-one',
      suggestion: `Skip one event to resolve travel conflict`,
      options: this.prioritizeEvents(events)
    });

    return resolutions;
  }

  generateParentAvailabilityResolutions(conflict, memberProfiles) {
    const resolutions = [];
    const parentProfiles = Array.from(memberProfiles.values())
      .filter(p => p.role === 'parent');

    // Find available parents at nearby times
    parentProfiles.forEach(parent => {
      const nearbyAvailability = this.findNearbyAvailability(parent, conflict.event);
      if (nearbyAvailability) {
        resolutions.push({
          action: 'reschedule-with-parent',
          parent: parent.member,
          originalTime: conflict.event.time,
          suggestedTime: nearbyAvailability.time,
          timeDifference: nearbyAvailability.difference,
          feasibility: 0.8
        });
      }
    });

    // Alternative supervision
    if (conflict.child.age >= 13) {
      resolutions.push({
        action: 'independent-attendance',
        suggestion: `${conflict.child.name} could attend independently`,
        requirements: ['Ensure transportation', 'Check-in protocol'],
        feasibility: 0.6
      });
    }

    return resolutions;
  }

  /**
   * Utility methods
   */
  getMemberEvents(memberId, calendar) {
    return calendar?.filter(event => 
      event.participants?.some(p => p.id === memberId)
    ) || [];
  }

  getMemberTasks(memberId, tasks) {
    return tasks?.filter(task => 
      task.assigned_to === memberId || task.created_by === memberId
    ) || [];
  }

  eventsOverlap(event1, event2) {
    const start1 = new Date(event1.date + ' ' + (event1.time || '00:00'));
    const end1 = addMinutes(start1, event1.duration || 60);
    const start2 = new Date(event2.date + ' ' + (event2.time || '00:00'));
    const end2 = addMinutes(start2, event2.duration || 60);
    
    return start1 < end2 && end1 > start2;
  }

  calculateOverlap(event1, event2) {
    const start1 = new Date(event1.date + ' ' + (event1.time || '00:00'));
    const end1 = addMinutes(start1, event1.duration || 60);
    const start2 = new Date(event2.date + ' ' + (event2.time || '00:00'));
    const end2 = addMinutes(start2, event2.duration || 60);
    
    const overlapStart = new Date(Math.max(start1, start2));
    const overlapEnd = new Date(Math.min(end1, end2));
    
    return differenceInMinutes(overlapEnd, overlapStart);
  }

  estimateTravelTime(location1, location2) {
    // Simplified estimation - would integrate with maps API
    if (!location1 || !location2) return 0;
    if (location1 === location2) return 0;
    if (location1 === 'Home' || location2 === 'Home') return 15;
    return 25; // Default travel time
  }

  calculateEventGap(event1, event2) {
    const end1 = addMinutes(
      new Date(event1.date + ' ' + (event1.time || '00:00')),
      event1.duration || 60
    );
    const start2 = new Date(event2.date + ' ' + (event2.time || '00:00'));
    return differenceInMinutes(start2, end1);
  }

  isParentAvailable(parentProfile, event) {
    const eventStart = new Date(event.date + ' ' + (event.time || '00:00'));
    const eventEnd = addMinutes(eventStart, event.duration || 60);
    
    // Check if parent has conflicting events
    return !parentProfile.events.some(parentEvent => {
      const parentStart = new Date(parentEvent.date + ' ' + (parentEvent.time || '00:00'));
      const parentEnd = addMinutes(parentStart, parentEvent.duration || 60);
      return parentStart < eventEnd && parentEnd > eventStart;
    });
  }

  generateTimeSlots() {
    const slots = [];
    const now = new Date();
    
    // Generate slots for next 7 days
    for (let day = 0; day < 7; day++) {
      const date = addMinutes(now, day * 24 * 60);
      
      // Morning slot (8am-12pm)
      slots.push({
        date: format(date, 'yyyy-MM-dd'),
        start: '08:00',
        end: '12:00',
        duration: 240,
        period: 'morning'
      });
      
      // Afternoon slot (2pm-6pm)
      slots.push({
        date: format(date, 'yyyy-MM-dd'),
        start: '14:00',
        end: '18:00',
        duration: 240,
        period: 'afternoon'
      });
      
      // Evening slot (6pm-9pm)
      slots.push({
        date: format(date, 'yyyy-MM-dd'),
        start: '18:00',
        end: '21:00',
        duration: 180,
        period: 'evening'
      });
    }
    
    return slots;
  }

  isMemberAvailable(profile, slot) {
    const slotStart = new Date(`${slot.date} ${slot.start}`);
    const slotEnd = new Date(`${slot.date} ${slot.end}`);
    
    return !profile.events.some(event => {
      const eventStart = new Date(event.date + ' ' + (event.time || '00:00'));
      const eventEnd = addMinutes(eventStart, event.duration || 60);
      return eventStart < slotEnd && eventEnd > slotStart;
    });
  }

  assessTimeSlotQuality(slot, members) {
    let quality = 0.5;
    
    // Weekend bonus
    const dayOfWeek = new Date(slot.date).getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) quality += 0.2;
    
    // Evening bonus for family time
    if (slot.period === 'evening') quality += 0.1;
    
    // All members available bonus
    if (members.length >= 4) quality += 0.2;
    
    return Math.min(quality, 1.0);
  }

  rankConflictsBySeverity(conflicts) {
    const severityOrder = { high: 3, medium: 2, low: 1 };
    return conflicts.sort((a, b) => 
      severityOrder[b.severity] - severityOrder[a.severity]
    );
  }

  rankOpportunitiesByImpact(opportunities) {
    const impactOrder = { high: 3, medium: 2, low: 1 };
    return opportunities.sort((a, b) => 
      impactOrder[b.impact] - impactOrder[a.impact]
    );
  }

  calculateCoordinationScore(memberProfiles, conflicts) {
    const totalMembers = memberProfiles.size;
    const totalConflicts = conflicts.length;
    const highSeverityConflicts = conflicts.filter(c => c.severity === 'high').length;
    
    // Base score
    let score = 1.0;
    
    // Deduct for conflicts
    score -= (totalConflicts * 0.05);
    score -= (highSeverityConflicts * 0.1);
    
    // Consider member availability
    const avgAvailability = Array.from(memberProfiles.values())
      .reduce((sum, profile) => sum + profile.availability.score, 0) / totalMembers;
    score *= avgAvailability;
    
    return Math.max(0, Math.min(1, score));
  }

  generateCoordinationInsights(memberProfiles, conflicts, opportunities) {
    const insights = [];
    
    // Workload imbalance
    const workloads = this.calculateMemberWorkloads(memberProfiles);
    const maxWorkload = Math.max(...workloads.map(w => w.score));
    const minWorkload = Math.min(...workloads.map(w => w.score));
    
    if (maxWorkload - minWorkload > 0.5) {
      insights.push({
        type: 'workload-imbalance',
        severity: 'medium',
        message: 'Significant workload imbalance detected in family',
        suggestion: 'Consider redistributing tasks and responsibilities'
      });
    }
    
    // Conflict patterns
    if (conflicts.filter(c => c.type === 'logistics-conflict').length > 3) {
      insights.push({
        type: 'logistics-challenge',
        severity: 'high',
        message: 'Multiple logistics conflicts detected',
        suggestion: 'Review activity locations and travel times'
      });
    }
    
    // Missed opportunities
    if (opportunities.filter(o => o.type === 'family-time').length > 5) {
      insights.push({
        type: 'family-time-available',
        severity: 'low',
        message: 'Multiple family time opportunities available',
        suggestion: 'Schedule regular family activities'
      });
    }
    
    return insights;
  }

  analyzeCommitments(events, tasks) {
    const totalCommitments = events.length + tasks.length;
    const highPriorityCommitments = [
      ...events.filter(e => e.priority === 'high'),
      ...tasks.filter(t => t.priority === 'high')
    ].length;
    
    return {
      total: totalCommitments,
      highPriority: highPriorityCommitments,
      distribution: this.analyzeCommitmentDistribution(events, tasks)
    };
  }

  calculateAvailability(events) {
    const totalHours = 24 * 7; // Weekly hours
    const committedHours = events.reduce((sum, event) => 
      sum + (event.duration || 60) / 60, 0
    );
    
    return {
      score: 1 - (committedHours / totalHours),
      freeHours: totalHours - committedHours,
      peakTimes: this.identifyPeakAvailability(events)
    };
  }

  extractPreferences(member, events) {
    // Analyze patterns in event scheduling
    const timingPreferences = this.analyzeTimingPreferences(events);
    const activityPreferences = this.analyzeActivityPreferences(events);
    
    return {
      preferredTimes: timingPreferences,
      preferredActivities: activityPreferences,
      flexibility: this.assessScheduleFlexibility(events)
    };
  }

  // Additional helper methods would be implemented here...
  analyzeCommitmentDistribution(events, tasks) {
    return {
      morning: events.filter(e => parseInt(e.time?.split(':')[0] || 0) < 12).length,
      afternoon: events.filter(e => {
        const hour = parseInt(e.time?.split(':')[0] || 0);
        return hour >= 12 && hour < 17;
      }).length,
      evening: events.filter(e => parseInt(e.time?.split(':')[0] || 0) >= 17).length
    };
  }

  identifyPeakAvailability(events) {
    // Simplified - would analyze gaps in schedule
    return ['morning', 'early-afternoon'];
  }

  analyzeTimingPreferences(events) {
    // Analyze when events are typically scheduled
    const timeCounts = {};
    events.forEach(event => {
      const hour = parseInt(event.time?.split(':')[0] || 0);
      const period = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
      timeCounts[period] = (timeCounts[period] || 0) + 1;
    });
    
    return Object.entries(timeCounts)
      .sort(([,a], [,b]) => b - a)
      .map(([period]) => period);
  }

  analyzeActivityPreferences(events) {
    const categories = {};
    events.forEach(event => {
      if (event.category) {
        categories[event.category] = (categories[event.category] || 0) + 1;
      }
    });
    
    return Object.entries(categories)
      .sort(([,a], [,b]) => b - a)
      .map(([category]) => category);
  }

  assessScheduleFlexibility(events) {
    const flexibleEvents = events.filter(e => e.flexible || !e.required).length;
    return flexibleEvents / Math.max(events.length, 1);
  }

  calculateMemberWorkloads(memberProfiles) {
    return Array.from(memberProfiles.values()).map(profile => ({
      profile,
      score: (profile.commitments.total / 20), // Normalize to 0-1
      details: {
        events: profile.events.length,
        tasks: profile.tasks.length,
        highPriority: profile.commitments.highPriority
      }
    }));
  }

  identifyDelegatableTasks(profile) {
    return profile.tasks.filter(task => 
      task.delegatable !== false && 
      task.priority !== 'high' &&
      !task.personalOnly
    );
  }

  canMemberHandleTask(profile, task) {
    // Check if member has skills/permissions for task
    if (task.requiresAdult && profile.role !== 'parent') return false;
    if (task.requiresDriving && !profile.member.canDrive) return false;
    
    // Check if member has time
    const availability = profile.availability.score;
    return availability > 0.3;
  }

  prioritizeOptimizations(optimizations) {
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    return optimizations.sort((a, b) => {
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
      if (priorityDiff !== 0) return priorityDiff;
      
      // Sort by estimated impact/benefit
      const aValue = a.estimatedImpact || a.estimatedBenefit || 0;
      const bValue = b.estimatedImpact || b.estimatedBenefit || 0;
      return bValue - aValue;
    });
  }

  // Missing helper methods
  countWeeklyCommitments(events) {
    // Count events for the current week
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate >= oneWeekAgo;
    }).length;
  }

  calculateAverageDailyBusy(events) {
    if (events.length === 0) return 0;
    
    // Group events by day
    const eventsByDay = new Map();
    events.forEach(event => {
      const day = event.date;
      if (!eventsByDay.has(day)) {
        eventsByDay.set(day, []);
      }
      eventsByDay.get(day).push(event);
    });

    // Calculate total busy hours per day
    const dailyBusyHours = Array.from(eventsByDay.values()).map(dayEvents => {
      return dayEvents.reduce((sum, event) => sum + (event.duration || 60) / 60, 0);
    });

    // Return average
    return dailyBusyHours.reduce((sum, hours) => sum + hours, 0) / dailyBusyHours.length;
  }

  identifyPreferredTimes(events) {
    const timeSlots = {
      morning: 0,
      afternoon: 0,
      evening: 0
    };

    events.forEach(event => {
      const hour = parseInt(event.time?.split(':')[0] || 0);
      if (hour < 12) {
        timeSlots.morning++;
      } else if (hour < 17) {
        timeSlots.afternoon++;
      } else {
        timeSlots.evening++;
      }
    });

    // Return preferred times sorted by frequency
    return Object.entries(timeSlots)
      .sort(([,a], [,b]) => b - a)
      .map(([time]) => time);
  }

  generateFamilyActivitySuggestions(slot, activities) {
    const suggestions = [];
    
    // Base suggestions by time of day
    const timeBasedSuggestions = {
      morning: [
        'Family breakfast together',
        'Weekend farmers market visit',
        'Nature walk or hike',
        'Grocery shopping together'
      ],
      afternoon: [
        'Board game tournament',
        'Cooking project together',
        'Outdoor sports or activities',
        'Art or craft time'
      ],
      evening: [
        'Movie night',
        'Family dinner and conversation',
        'Reading time together',
        'Puzzle solving'
      ]
    };

    // Get suggestions based on time slot
    const baseSuggestions = timeBasedSuggestions[slot.period] || timeBasedSuggestions.afternoon;
    suggestions.push(...baseSuggestions.slice(0, 2));

    // Add activity-based suggestions if activities provided
    if (activities && activities.length > 0) {
      const familyFriendlyActivities = activities
        .filter(activity => activity.category === 'family' || activity.suitable_for_all)
        .slice(0, 2)
        .map(activity => activity.title || activity.name);
      
      suggestions.push(...familyFriendlyActivities);
    }

    // Add seasonal/weather-based suggestions
    const now = new Date();
    const month = now.getMonth();
    const season = month >= 3 && month <= 5 ? 'spring' :
                  month >= 6 && month <= 8 ? 'summer' :
                  month >= 9 && month <= 11 ? 'fall' : 'winter';

    const seasonalSuggestions = {
      spring: ['Garden planning', 'Spring cleaning together'],
      summer: ['Pool time', 'BBQ preparation'],
      fall: ['Leaf pile jumping', 'Halloween decorating'],
      winter: ['Hot cocoa making', 'Holiday planning']
    };

    if (seasonalSuggestions[season]) {
      suggestions.push(seasonalSuggestions[season][0]);
    }

    // Return unique suggestions
    return [...new Set(suggestions)].slice(0, 4);
  }

  groupEventsByTimeAndLocation(events) {
    const windows = [];
    const timeWindow = 30; // 30 minutes window for grouping
    
    // Sort events by date and time
    const sortedEvents = events.sort((a, b) => {
      const dateA = new Date(`${a.date} ${a.time || '00:00'}`);
      const dateB = new Date(`${b.date} ${b.time || '00:00'}`);
      return dateA - dateB;
    });

    sortedEvents.forEach(event => {
      const eventTime = new Date(`${event.date} ${event.time || '00:00'}`);
      
      // Find existing window this event could join
      let foundWindow = windows.find(window => {
        const windowTime = new Date(`${window.date} ${window.time}`);
        const timeDiff = Math.abs(eventTime - windowTime) / (1000 * 60); // minutes
        return timeDiff <= timeWindow && 
               window.location === event.location &&
               window.date === event.date;
      });

      if (foundWindow) {
        foundWindow.events.push(event);
      } else {
        // Create new window
        windows.push({
          date: event.date,
          time: event.time || '00:00',
          location: event.location,
          events: [event]
        });
      }
    });

    return windows;
  }

  estimateCarpoolTimeSaved(events) {
    if (events.length < 2) return 0;
    
    // Simple calculation: assume each individual trip takes 30 minutes
    // Carpool saves all but one trip
    const individualTripTime = 30; // minutes
    const savedTrips = events.length - 1;
    return savedTrips * individualTripTime;
  }

  calculateTotalErrandTime(errands) {
    return errands.reduce((total, errand) => {
      return total + (errand.duration || 60) + 15; // Add 15min travel between errands
    }, 0);
  }

  calculateBatchedErrandTime(errands) {
    if (errands.length === 0) return 0;
    
    // Batched errands share travel time
    const totalErrandTime = errands.reduce((sum, errand) => sum + (errand.duration || 60), 0);
    const travelTime = 15 * Math.max(1, errands.length - 1); // Travel between errands
    return totalErrandTime + travelTime;
  }

  calculateErrandTimeSaved(errands) {
    const individualTime = this.calculateTotalErrandTime(errands);
    const batchedTime = this.calculateBatchedErrandTime(errands);
    return Math.max(0, individualTime - batchedTime);
  }

  groupErrandsByProximity(errands) {
    // Simple grouping by location similarity
    const groups = [];
    
    errands.forEach(errand => {
      let foundGroup = groups.find(group => 
        group.some(e => this.locationsAreNear(e.location, errand.location))
      );
      
      if (foundGroup) {
        foundGroup.push(errand);
      } else {
        groups.push([errand]);
      }
    });
    
    return groups;
  }

  locationsAreNear(location1, location2) {
    if (!location1 || !location2) return false;
    if (location1 === location2) return true;
    
    // Simple string similarity for grouping
    const similarity = this.calculateLocationSimilarity(location1, location2);
    return similarity > 0.6;
  }

  calculateLocationSimilarity(loc1, loc2) {
    if (!loc1 || !loc2) return 0;
    
    const words1 = loc1.toLowerCase().split(/\s+/);
    const words2 = loc2.toLowerCase().split(/\s+/);
    
    const commonWords = words1.filter(word => words2.includes(word));
    const totalWords = new Set([...words1, ...words2]).size;
    
    return commonWords.length / totalWords;
  }

  generateOpportunityActions(opportunity, memberProfiles) {
    const actions = [];
    
    switch (opportunity.type) {
      case 'family-time':
        actions.push({
          action: 'schedule-family-activity',
          description: `Schedule family time for ${opportunity.timeSlot.period}`,
          participants: opportunity.availableMembers || [],
          feasibility: 0.8
        });
        break;
        
      case 'carpool':
        actions.push({
          action: 'organize-carpool',
          description: `Coordinate carpool with ${opportunity.potentialDriver.name} driving`,
          participants: opportunity.participants,
          timeSaved: opportunity.timeSaved,
          feasibility: 0.7
        });
        break;
        
      case 'delegation':
        actions.push({
          action: 'delegate-tasks',
          description: `Delegate ${opportunity.tasks.length} tasks from ${opportunity.from.name} to ${opportunity.to.name}`,
          tasks: opportunity.tasks,
          feasibility: 0.6
        });
        break;
        
      case 'errand-batching':
        actions.push({
          action: 'batch-errands',
          description: `Combine ${opportunity.errands.length} errands to save ${opportunity.timeSaved} minutes`,
          errands: opportunity.errands,
          timeSaved: opportunity.timeSaved,
          feasibility: 0.9
        });
        break;
        
      default:
        actions.push({
          action: 'general-optimization',
          description: 'Optimize schedule coordination',
          feasibility: 0.5
        });
    }
    
    return actions;
  }

  estimateResolutionImpact(conflict, resolutions) {
    if (resolutions.length === 0) return 0;
    
    // Base impact on conflict severity
    const severityImpact = {
      high: 0.9,
      medium: 0.6,
      low: 0.3
    };
    
    const baseImpact = severityImpact[conflict.severity] || 0.5;
    const avgFeasibility = resolutions.reduce((sum, r) => sum + (r.feasibility || 0.5), 0) / resolutions.length;
    
    return baseImpact * avgFeasibility;
  }

  estimateOpportunityBenefit(opportunity) {
    const impactScores = {
      high: 0.8,
      medium: 0.5,
      low: 0.2
    };
    
    let benefit = impactScores[opportunity.impact] || 0.3;
    
    // Add specific benefits
    if (opportunity.timeSaved) {
      benefit += Math.min(0.3, opportunity.timeSaved / 100); // Max 0.3 bonus for time saved
    }
    
    if (opportunity.workloadReduction) {
      benefit += opportunity.workloadReduction * 0.2;
    }
    
    return Math.min(1.0, benefit);
  }

  generateProactiveOptimizations(memberProfiles) {
    const optimizations = [];
    const allProfiles = Array.from(memberProfiles.values());
    
    // Suggest routine establishment
    const lowRoutineMembers = allProfiles.filter(p => 
      p.preferences.flexibility < 0.3
    );
    
    if (lowRoutineMembers.length > 0) {
      optimizations.push({
        type: 'routine-establishment',
        priority: 'medium',
        description: 'Establish more consistent routines for better coordination',
        targetMembers: lowRoutineMembers.map(p => p.member),
        estimatedBenefit: 0.6
      });
    }
    
    // Suggest communication improvements
    if (allProfiles.length > 2) {
      optimizations.push({
        type: 'communication-enhancement',
        priority: 'low',
        description: 'Set up regular family coordination check-ins',
        estimatedBenefit: 0.4
      });
    }
    
    return optimizations;
  }

  canReschedule(event) {
    // Events that typically can be rescheduled
    if (event.flexible === true) return true;
    if (event.type === 'personal' || event.category === 'personal') return true;
    if (event.priority === 'low') return true;
    
    // Events that typically cannot be rescheduled
    if (event.type === 'work' || event.category === 'work') return false;
    if (event.type === 'school' || event.category === 'school') return false;
    if (event.priority === 'high') return false;
    
    // Default to partially reschedulable
    return 0.5;
  }

  assessRescheduleFeasibility(event, minutesShift) {
    let feasibility = 0.5;
    
    // Smaller shifts are more feasible
    const shiftPenalty = Math.abs(minutesShift) / 60; // Per hour penalty
    feasibility -= shiftPenalty * 0.2;
    
    // Event type affects feasibility
    if (event.flexible) feasibility += 0.3;
    if (event.priority === 'low') feasibility += 0.2;
    if (event.priority === 'high') feasibility -= 0.3;
    
    return Math.max(0, Math.min(1, feasibility));
  }

  suggestAlternativeResources(resource) {
    const alternatives = {
      'car': ['rideshare', 'public transport', 'bike', 'walk'],
      'room': ['alternate room', 'outdoor space', 'virtual meeting'],
      'equipment': ['rent equipment', 'borrow from neighbor', 'alternative method']
    };
    
    return alternatives[resource] || ['find alternative', 'reschedule'];
  }

  generateStaggeredSchedule(events) {
    const schedule = [];
    let currentTime = new Date(events[0].date + ' ' + events[0].time);
    
    events.forEach((event, index) => {
      schedule.push({
        event: event.title,
        suggestedTime: format(currentTime, 'HH:mm'),
        duration: event.duration || 60
      });
      
      // Add buffer time between events
      currentTime = addMinutes(currentTime, (event.duration || 60) + 15);
    });
    
    return schedule;
  }

  canLeaveEarly(event, minutesEarly) {
    // Work/school events are harder to leave early
    if (event.type === 'work' || event.category === 'work') return minutesEarly <= 15;
    if (event.type === 'school' || event.category === 'school') return minutesEarly <= 10;
    
    // Personal events are more flexible
    if (event.type === 'personal') return minutesEarly <= 30;
    
    // Social events depend on priority
    if (event.priority === 'low') return minutesEarly <= 45;
    if (event.priority === 'high') return minutesEarly <= 5;
    
    return minutesEarly <= 20; // Default
  }

  prioritizeEvents(events) {
    return events
      .map(event => ({
        event,
        score: this.calculateEventPriority(event)
      }))
      .sort((a, b) => b.score - a.score)
      .map(item => item.event);
  }

  calculateEventPriority(event) {
    let score = 0.5;
    
    // Priority level
    if (event.priority === 'high') score += 0.4;
    if (event.priority === 'low') score -= 0.2;
    
    // Event type importance
    if (event.type === 'work' || event.type === 'school') score += 0.3;
    if (event.type === 'medical') score += 0.4;
    if (event.type === 'personal') score -= 0.1;
    
    // Flexibility (inverse - less flexible = higher priority)
    if (event.flexible === false) score += 0.2;
    if (event.flexible === true) score -= 0.1;
    
    return Math.max(0, Math.min(1, score));
  }

  findNearbyAvailability(parentProfile, event) {
    const eventTime = new Date(event.date + ' ' + event.time);
    const searchWindow = 120; // 2 hours
    
    // Check time slots around the event
    for (let offset = -searchWindow; offset <= searchWindow; offset += 30) {
      const checkTime = addMinutes(eventTime, offset);
      const checkTimeStr = format(checkTime, 'HH:mm');
      
      // Create a test event to check availability
      const testEvent = {
        date: event.date,
        time: checkTimeStr,
        duration: event.duration || 60
      };
      
      if (this.isParentAvailable(parentProfile, testEvent)) {
        return {
          time: checkTimeStr,
          difference: Math.abs(offset)
        };
      }
    }
    
    return null;
  }
}

// Export singleton instance
export const familyCoordinationService = new FamilyCoordinationService();
export default familyCoordinationService;